import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import type { PressEvent } from 'react-aria';
import { Provider, useSlottedContext } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { assert } from '@gitbook/util-ts';

import { Actions } from '../Actions';
import { AnimatedStateContext } from '../animations';
import brandedModalImageDarkSrc from '../assets/branded-modal-asset-dark.png';
import brandedModalImageLightSrc from '../assets/branded-modal-asset-light.png';
import { Backdrop } from '../Backdrop';
import { Button, type ButtonProps } from '../Button';
import { DescriptionContext } from '../Description';
import { HeadingContext } from '../Heading';
import {
    Overlay,
    OverlayCloseButton,
    type OverlayCloseButtonProps,
    OverlayContext,
    type OverlayProps,
    OverlayTrigger,
    type OverlayTriggerProps,
    useOverlayAction,
    useOverlayClose,
} from '../Overlay';
import { Portal } from '../Portal';
import { pxToRem } from '../util';
import { DialogStackProvider, useStackDialog } from './DialogStack';

export { OverlayRoot as DialogRoot } from '../Overlay';

export type DialogTriggerProps = Omit<OverlayTriggerProps, 'ariaHasPopup'>;

export function DialogTrigger(props: DialogTriggerProps) {
    return (
        <OverlayTrigger trigger={props.trigger} ariaHasPopup="dialog">
            {props.children}
        </OverlayTrigger>
    );
}

type AbstractDialogProps = {
    /**
     * The role of the dialog.
     */
    role: 'dialog' | 'alertdialog';

    /**
     * Set the size of the dialog.
     * - `full-screen` will make the dialog take the full screen.
     * - `auto` will make the dialog take the size of its content.
     * - `{ width?, height? }` sets the dialog to a specific width, height or both.
     * - `undefined` will make the dialog take the default size.
     */
    size?:
        | 'full-screen'
        | 'auto'
        | { width?: string | number; height?: string | number }
        | undefined;

    children: React.ReactNode;

    className?: string;
} & Pick<OverlayProps, 'detached'>;

/**
 * Abstract dialog that exposes a "role" prop and contains dialog logic.
 */
function AbstractDialog(props: AbstractDialogProps) {
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Dialog must be used within a DialogRoot');

    const [positionInStack, isRoot] = useStackDialog({ id: context.id, animatedState });

    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';

    /* We visualize max 3 dialogs in the stack. Any more will take the position of the third, but question the design if you encounter this as stacking dialogs isn't the best UX. */
    const maxPositionInStack = Math.min(positionInStack, 2);

    return (
        <Provider
            values={[
                [HeadingContext, { level: 2, id: headingId, className: 'text-ui-heading-large' }],
                [DescriptionContext, { id: descriptionId }],
            ]}
        >
            {isRoot && <Backdrop isExiting={isExiting} isEntering={isEntering} />}
            <Overlay
                detached={props.detached}
                role={props.role}
                id={context.id}
                aria-modal="true"
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                style={
                    {
                        '--dialog-max-height': 'calc(100vh - 2rem)',
                        height:
                            typeof props.size === 'object' && props.size && props.size.height
                                ? `min(var(--dialog-max-height), ${pxToRem(props.size.height)})`
                                : undefined,
                        width:
                            typeof props.size === 'object' && props.size && props.size.width
                                ? '100%'
                                : undefined,
                        maxWidth:
                            typeof props.size === 'object' && props.size && props.size.width
                                ? pxToRem(props.size.width)
                                : undefined,
                    } as React.CSSProperties
                }
                className={clsx(
                    'group/dialog',
                    'relative max-h-[--dialog-max-height] shrink-0 overflow-x-auto rounded-lg bg-base will-change-transform',
                    'fill-mode-forwards transition [transition-delay:30ms] [transition-duration:150ms] [transition-timing-function:ease-out]',
                    !props.size && 'w-full max-w-xl',
                    props.size === 'full-screen' && 'size-full',
                    isEntering && 'fade-in slide-in-from-top-2 zoom-in-95 animate-in',
                    isExiting && 'fade-out slide-out-to-top-2 zoom-out-95 animate-out',
                    /* Scale and translate on Y-axis to make the stack order visual */
                    maxPositionInStack === 1 && '-translate-y-8 scale-95 brightness-75',
                    maxPositionInStack === 2 && '-translate-y-16 scale-90 brightness-50',
                    props.className
                )}
            >
                {context.isClosable ? (
                    <div className="sticky top-0 z-10">
                        <DialogCloseButton
                            className="absolute top-3 right-3 z-[1]"
                            size="small"
                            iconOnly
                            label="Close"
                            kind="blank"
                            leadingIcon={Icon.X}
                        />
                    </div>
                ) : null}
                {props.children}
            </Overlay>
        </Provider>
    );
}

type ModalProps = {
    children: React.ReactNode;
};

function Modal(props: ModalProps) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Dialog must be used within a DialogRoot');

    if (animatedState === 'exited') {
        return null;
    }

    return (
        <Portal data-spine-modal>
            <div className="fixed inset-0 z-[100] flex flex-col items-center p-4">
                <div className="h-[8vh] shrink-[100]" />
                <DialogStackProvider>{props.children}</DialogStackProvider>
                <div className="h-[8vh] shrink-[100]" />
            </div>
        </Portal>
    );
}

export type DialogProps = Omit<AbstractDialogProps, 'role'>;

export function Dialog(props: DialogProps) {
    return (
        <Modal>
            <AbstractDialog role="dialog" {...props} />
        </Modal>
    );
}

export type AlertDialogProps = {
    children?: React.ReactNode;
};

/**
 * A modal dialog that interrupts the user with important content and expects a response.
 */
export function AlertDialog(props: AlertDialogProps) {
    return (
        <Modal>
            <AbstractDialog role="alertdialog">{props.children}</AbstractDialog>
        </Modal>
    );
}

export type DialogHeaderProps = React.ComponentPropsWithoutRef<'div'>;

export function DialogHeader(props: DialogHeaderProps) {
    const isDialogBrandedLayout = React.useContext(DialogBrandedLayoutContext);

    return (
        <div
            {...props}
            sp-dialog-header=""
            className={clsx(
                'flex flex-col gap-2 px-4 md:px-10',
                isDialogBrandedLayout
                    ? 'pt-2'
                    : clsx('py-6 md:py-10', 'group-has-[[sp-dialog-body]]/dialog:pb-0'),
                props.className
            )}
        />
    );
}

export type DialogBodyProps = React.ComponentPropsWithoutRef<'div'> & {
    /**
     * Set the body to be scrollable.
     * @default true
     */
    scrollable?: boolean;
};

export function DialogBody(props: DialogBodyProps) {
    const { scrollable = true, ...rest } = props;
    const isDialogBrandedLayout = React.useContext(DialogBrandedLayoutContext);

    return (
        <Provider
            values={[
                [HeadingContext, {}],
                [DescriptionContext, {}],
            ]}
        >
            <div
                {...rest}
                sp-dialog-body=""
                className={clsx(
                    'relative box-border w-full px-4 md:px-10',
                    scrollable && 'scrollbar overflow-y-auto',
                    isDialogBrandedLayout
                        ? 'pt-2 pb-6'
                        : clsx('py-6 md:py-10', 'group-has-[[sp-dialog-header]]/dialog:pt-6'),
                    props.className
                )}
            />
        </Provider>
    );
}

export type DialogFooterProps = React.ComponentPropsWithoutRef<'div'>;

export function DialogFooter(props: DialogFooterProps) {
    return (
        <Actions
            {...props}
            sp-dialog-footer=""
            className={clsx(
                // The bottom needs to be a margin to stay "within"
                // the dialog and show its rounded corners.
                'sticky bottom-0 w-full border-t bg-base px-4 py-4',
                props.className
            )}
        />
    );
}

export type DialogFooterSecondaryProps = React.ComponentPropsWithoutRef<'div'>;

export function DialogFooterSecondary(props: DialogFooterSecondaryProps) {
    return <div {...props} className={clsx('flex flex-1 items-center gap-4', props.className)} />;
}

export type DialogCloseButtonProps = Omit<OverlayCloseButtonProps, 'label'> & { label?: string };

export function DialogCloseButton(props: DialogCloseButtonProps) {
    return (
        <OverlayCloseButton
            {...props}
            label={props.label ?? 'Cancel'}
            kind={props.kind ?? 'secondary'}
        />
    );
}

const DialogBrandedLayoutContext = React.createContext<boolean>(false);

export interface DialogBrandedLayoutProps {
    children: React.ReactNode;

    /**
     * Configuration for the aside panel
     */
    aside?: {
        /** Content placed on top of the gradient */
        content?: React.ReactNode;

        /** Overrides for gradients */
        gradient?: {
            light: string;
            dark: string;
        };
    };

    /**
     * Optional logo to display in the dialog.
     * Defaults to the GitBook logo.
     */
    logo?: React.ReactNode | false;
}

/**
 * Apply a 2-panel layout with a gradient on the left.
 */
export function DialogBrandedLayout({ children, aside, logo }: DialogBrandedLayoutProps) {
    const headingProps = useSlottedContext(HeadingContext);
    return (
        <DialogBrandedLayoutContext.Provider value>
            <HeadingContext.Provider
                value={{ ...headingProps, className: 'text-ui-heading-medium' }}
            >
                <div className="flex max-md:flex-col md:h-full">
                    <div
                        className={clsx(
                            'flex w-56 flex-col justify-end bg-[image:--light-bg] bg-base bg-cover bg-right-bottom bg-no-repeat p-5 max-md:sticky max-md:bottom-0 max-md:order-2 max-md:w-full max-md:border-t md:h-full lg:w-80 dark:bg-[image:--dark-bg]',
                            aside ? 'flex' : 'flex max-md:hidden'
                        )}
                        style={
                            {
                                '--light-bg': `url(${aside?.gradient?.light || brandedModalImageLightSrc})`,
                                '--dark-bg': `url(${aside?.gradient?.dark || brandedModalImageDarkSrc})`,
                            } as React.CSSProperties
                        }
                    >
                        {aside?.content}
                    </div>
                    <div className="flex flex-1 flex-col items-start pt-10">
                        <div className="shrink-0 px-4 pb-2 max-md:hidden md:px-10">
                            {logo === false ? null : logo || <Icon.LogoSquare size={32} />}
                        </div>
                        {children}
                    </div>
                </div>
                {aside && (
                    <>
                        <link
                            href={aside.gradient?.light || brandedModalImageLightSrc}
                            as="image"
                            rel="preload"
                        />
                        <link
                            href={aside.gradient?.dark || brandedModalImageLightSrc}
                            as="image"
                            rel="preload"
                        />
                    </>
                )}
            </HeadingContext.Provider>
        </DialogBrandedLayoutContext.Provider>
    );
}

/**
 * The aside of a branded dialog.
 */
export function BrandedDialogAside(props: {
    children: React.ReactNode;
    backgroundSrc?: { light?: string; dark?: string };
}) {
    const { children, backgroundSrc } = props;

    const lightBackgroundSrc = backgroundSrc?.light ?? brandedModalImageLightSrc;
    const darkBackgroundSrc = backgroundSrc?.dark ?? brandedModalImageDarkSrc;

    return (
        <>
            <div
                className="flex flex-col justify-end bg-[image:--light-bg] bg-base bg-cover bg-right-bottom bg-no-repeat p-5 max-md:sticky max-md:bottom-0 max-md:order-2 max-md:border-t md:min-h-full md:w-72 dark:bg-[image:--dark-bg]"
                style={
                    {
                        '--light-bg': `url(${lightBackgroundSrc})`,
                        '--dark-bg': `url(${darkBackgroundSrc})`,
                    } as React.CSSProperties
                }
            >
                {children}
            </div>
            <link href={lightBackgroundSrc} as="image" rel="preload" />
            <link href={darkBackgroundSrc} as="image" rel="preload" />
        </>
    );
}

export type DialogActionButtonProps = Omit<ButtonProps, 'onPress'> & {
    /**
     * The action to perform when the confirm button is pressed.
     */
    onPress: (
        event: PressEvent,
        options: {
            /**
             * Prevent the dialog from closing.
             */
            preventClose: () => void;
        }
    ) => void | Promise<void>;
};

export function DialogActionButton(props: DialogActionButtonProps) {
    const { onPress, ...rest } = props;
    const [isPending, startDialogAction] = useOverlayAction();
    const close = useOverlayClose();
    return (
        <Button
            {...rest}
            loadable={[isPending]}
            onPress={(event) => {
                let shouldClose = true;
                const promise = onPress(event, {
                    preventClose: () => {
                        shouldClose = false;
                    },
                });
                // If it returns a promise, wait for it to resolve before closing the dialog.
                if (promise) {
                    return startDialogAction(async ({ close }) => {
                        await promise;
                        if (shouldClose) {
                            await close();
                        }
                    });
                }
                if (shouldClose) {
                    close();
                }
            }}
        />
    );
}
